import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PickBand from "./pages/PickBand";
import Dashboard from "./pages/Dashboard";
import Lesson from "./pages/Lesson";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Verify from "./pages/Verify";
import Meet from "./pages/Meet";
import MeetIndex from "./pages/MeetIndex";
import Showcase from "./pages/Showcase";
import Admin from "./pages/Admin";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import RoboticsLanding from "./pages/robotics/RoboticsLanding";
import RoboticsShowcase from "./pages/robotics/RoboticsShowcase";
import RoboticsMeet from "./pages/robotics/RoboticsMeet";
import RoboticsMeetDetail from "./pages/robotics/RoboticsMeetDetail";
import RoboticsDashboard from "./pages/robotics/RoboticsDashboard";
import RoboticsGirlsAtTec from "./pages/robotics/RoboticsGirlsAtTec";
import RoboticsForParents from "./pages/robotics/RoboticsForParents";
import RoboticsSchools from "./pages/robotics/RoboticsSchools";
import RoboticsSignin from "./pages/robotics/RoboticsSignin";
import RoboticsSignup from "./pages/robotics/RoboticsSignup";
import RoboticsLesson from "./pages/robotics/RoboticsLesson";
import RoboticsHeritage from "./pages/robotics/RoboticsHeritage";
import RoboticsDramaPlayer from "./pages/robotics/RoboticsDramaPlayer";
import RoboticsColophon from "./pages/robotics/RoboticsColophon";
import RoboticsQualifications from "./pages/robotics/RoboticsQualifications";
import "./App.css";

function useScrubPreviewParam() {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const key = url.searchParams.get("preview");
    if (!key) {
      // Nothing to do; make the ready-promise resolve immediately so pages
      // that await it don't hang.
      window.__ftgOwnerPreviewReady = Promise.resolve();
      return;
    }

    const strip = () => {
      url.searchParams.delete("preview");
      const clean = url.pathname + (url.search ? url.search : "") + url.hash;
      window.history.replaceState({}, "", clean);
    };

    const api = process.env.REACT_APP_BACKEND_URL || "";
    // Expose a ready-promise so pages (e.g. /showcase) can wait for the cookie
    // to land before polling /api/preview/status.
    window.__ftgOwnerPreviewReady = fetch(
      `${api}/api/preview?preview=${encodeURIComponent(key)}` +
      `&next=${encodeURIComponent(url.pathname || "/showcase")}`,
      { credentials: "include", redirect: "manual" }
    )
      .catch(() => {})
      .finally(strip);
  }, []);
}

function App() {
  useScrubPreviewParam();
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: "var(--ftg-font-body)" },
          }}
        />
        <Routes>
          {/* -------------------------------------------------------------
              Route table (Aug 2026 flip).
              -------------------------------------------------------------
              The production domain is college.futuretec.global, so the
              root URL now serves the College of AI and Advanced Robotics.
              The legacy STEAM curriculum lives at /steam. All existing
              STEAM deep links (/meet, /showcase, /verify/:id, /lesson/…,
              /dashboard, /login, /signup, /admin, /checkout/*) remain at
              their historical paths so bookmarks and PDF links continue
              to work. All existing /robotics/* deep links also remain in
              place; /robotics itself is a soft alias for /.
              ------------------------------------------------------------- */}
          <Route path="/" element={<RoboticsLanding />} />
          <Route path="/steam" element={<Landing />} />
          <Route path="/robotics" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify/:certId" element={<Verify />} />
          <Route path="/meet" element={<MeetIndex />} />
          <Route path="/meet/:slug" element={<Meet />} />
          <Route path="/showcase" element={<Showcase />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          {/* Robotics Academy — Level 2.5 isolation. Distinct palette,
              type system, tone. Backend still shares /api namespace via
              the /api/robotics/* prefix. */}
          <Route path="/robotics/showcase" element={<RoboticsShowcase />} />
          <Route path="/robotics/meet" element={<RoboticsMeet />} />
          <Route path="/robotics/meet/:slug" element={<RoboticsMeetDetail />} />
          <Route path="/robotics/dashboard" element={<RoboticsDashboard />} />
          <Route path="/robotics/girls-at-tec" element={<RoboticsGirlsAtTec />} />
          <Route path="/robotics/for-parents" element={<RoboticsForParents />} />
          <Route path="/robotics/schools" element={<RoboticsSchools />} />
          <Route path="/robotics/signin" element={<RoboticsSignin />} />
          <Route path="/robotics/signup" element={<RoboticsSignup />} />
          <Route path="/robotics/lesson/:id" element={<RoboticsLesson />} />
          <Route path="/robotics/heritage" element={<RoboticsHeritage />} />
          <Route path="/robotics/heritage/:slug" element={<RoboticsDramaPlayer />} />
          <Route path="/robotics/about" element={<RoboticsColophon />} />
          <Route path="/robotics/qualifications" element={<RoboticsQualifications />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pick-band"
            element={
              <ProtectedRoute>
                <PickBand />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <Lesson />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
